import { throttle, Platform, isDefined } from '@tyler-components-web/core';
import { TOOLTIP_CONSTANTS } from './tooltip-constants';
/**
 * The foundation class behind the tooltip component.
 */
var TooltipFoundation = /** @class */ (function () {
    function TooltipFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._delay = TOOLTIP_CONSTANTS.numbers.DEFAULT_DELAY;
        this._position = TOOLTIP_CONSTANTS.strings.DEFAULT_POSITION;
        this._isOpen = false;
        this._mouseOverListener = function (evt) { return _this._onMouseOver(evt); };
        this._mouseOutListener = function (evt) { return _this._onMouseOut(evt); };
        this._touchStartListener = function (evt) { return _this._onTouchStart(evt); };
        this._touchEndListener = function (evt) { return _this._onTouchEnd(evt); };
        this._scrollListener = throttle(function (evt) { return _this._onScroll(evt); }, 100);
        this._clickListener = function () { return _this._targetInteracted(); };
        this._mouseDownListener = function () { return _this._targetInteracted(); };
        this._dragListener = function () { return _this._targetInteracted(); };
    }
    TooltipFoundation.prototype.initialize = function () {
        this._adapter.initializeTargetElement(this._target);
        if (!this._adapter.hasTargetElement()) {
            throw new Error('Unable to locate target element.');
        }
        // Set a unique id for this tooltip (unless it already has one)
        this._identifier = this._adapter.getHostAttribute('id');
        if (!this._identifier) {
            this._identifier = this._getNextIdentifier();
            this._adapter.setHostAttribute('id', this._identifier);
        }
        this._addTargetListeners();
        this._adapter.initializeAccessibility(this._identifier);
    };
    TooltipFoundation.prototype.disconnect = function () {
        if (this._mouseOverTimeout) {
            window.clearTimeout(this._mouseOverTimeout);
            this._mouseOverTimeout = undefined;
        }
        if (this._touchTimeout) {
            window.clearTimeout(this._touchTimeout);
            this._touchTimeout = undefined;
        }
        this._adapter.destroy(this._identifier);
        if (this._isOpen) {
            this.hide();
        }
        if (this._adapter.hasTargetElement()) {
            this._removeTargetListeners();
        }
    };
    /**
     * Generates the next available tooltip identifier.
     */
    TooltipFoundation.prototype._getNextIdentifier = function () {
        return "tcw-tooltip-" + TooltipFoundation._tooltipIdentifier++;
    };
    /** Adds event listeners to the target element. */
    TooltipFoundation.prototype._addTargetListeners = function () {
        if (Platform.isMobile) {
            this._adapter.addTargetEventListener('touchstart', this._touchStartListener);
            this._adapter.addTargetEventListener('touchend', this._touchEndListener);
        }
        else {
            this._adapter.addTargetEventListener('mouseover', this._mouseOverListener);
            this._adapter.addTargetEventListener('mouseout', this._mouseOutListener);
        }
        this._adapter.addTargetEventListener('click', this._clickListener);
        this._adapter.addTargetEventListener('mousedown', this._mouseDownListener);
        this._adapter.addTargetEventListener('dragstart', this._dragListener);
    };
    /**
     * Removes the event listeners from the target element.
     * @param targetElement The target element instance.
     */
    TooltipFoundation.prototype._removeTargetListeners = function () {
        if (Platform.isMobile) {
            this._adapter.removeTargetEventListener('touchstart', this._touchStartListener);
            this._adapter.removeTargetEventListener('touchend', this._touchEndListener);
        }
        else {
            this._adapter.removeTargetEventListener('mouseover', this._mouseOverListener);
            this._adapter.removeTargetEventListener('mouseout', this._mouseOutListener);
        }
        this._adapter.removeTargetEventListener('click', this._clickListener);
        this._adapter.removeTargetEventListener('mousedown', this._mouseDownListener);
        this._adapter.removeTargetEventListener('dragstart', this._dragListener);
    };
    /**
     * Handles the touchstart event on the target element to detect long press.
     */
    TooltipFoundation.prototype._onTouchStart = function (evt) {
        var _this = this;
        this._touchTimeout = window.setTimeout(function () {
            _this._show();
            window.setTimeout(function () { return _this.hide(); }, TOOLTIP_CONSTANTS.numbers.LONGPRESS_VISIBILITY_DURATION);
            _this._touchTimeout = undefined;
        }, TOOLTIP_CONSTANTS.numbers.LONGPRESS_THRESHOLD);
    };
    /**
     * Handles the touchend event on the target element to cancel a long press action.
     */
    TooltipFoundation.prototype._onTouchEnd = function (evt) {
        if (this._touchTimeout) {
            this._clearTouchTimer();
        }
    };
    TooltipFoundation.prototype._clearTouchTimer = function () {
        window.clearTimeout(this._touchTimeout);
        this._touchTimeout = undefined;
    };
    /**
     * Handles the mouseover event on the target element.
     */
    TooltipFoundation.prototype._onMouseOver = function (evt) {
        var _this = this;
        if (this._isOpen || !this._adapter.hasTargetElement() || !this._adapter.isTargetElementConnected()) {
            return;
        }
        if (this._delay) {
            this._mouseOverTimeout = window.setTimeout(function () {
                _this._show();
                _this._mouseOverTimeout = undefined;
            }, this._delay);
        }
        else {
            this._show();
        }
    };
    /**
     * Handles the mouseout event on the target element.
     */
    TooltipFoundation.prototype._onMouseOut = function (evt) {
        if (this._mouseOverTimeout) {
            window.clearTimeout(this._mouseOverTimeout);
            this._mouseOverTimeout = undefined;
            return;
        }
        this.hide();
    };
    /**
     * Displays the tooltip.
     */
    TooltipFoundation.prototype._show = function () {
        var content;
        if (this._builder && typeof this._builder === 'function') {
            content = this._builder();
        }
        this._adapter.showTooltip(this._position, content);
        if (this._adapter.hasTooltipElement()) {
            this._isOpen = true;
            this._adapter.addWindowListener('scroll', this._scrollListener);
        }
    };
    /**
     * Hides the tooltip.
     */
    TooltipFoundation.prototype.hide = function () {
        if (!this._isOpen) {
            return;
        }
        if (this._touchTimeout) {
            this._clearTouchTimer();
        }
        this._isOpen = false;
        this._adapter.removeWindowListener('scroll', this._scrollListener);
        this._adapter.hideTooltip();
    };
    /**
     * Handles scrolling events when the tooltip is open.
     */
    TooltipFoundation.prototype._onScroll = function (evt) {
        if (!this._isOpen) {
            return;
        }
        this.hide();
    };
    TooltipFoundation.prototype._targetInteracted = function () {
        if (this._mouseOverTimeout) {
            window.clearTimeout(this._mouseOverTimeout);
        }
        if (this._isOpen) {
            this.hide();
        }
    };
    Object.defineProperty(TooltipFoundation.prototype, "text", {
        /** Gets/sets the tooltip text. */
        get: function () {
            return this._text || this._adapter.getInnerText();
        },
        set: function (value) {
            if (this._text !== value) {
                this._text = value;
                this._adapter.setTextContent(this._text);
                this._adapter.setHostAttribute(TOOLTIP_CONSTANTS.attributes.TEXT, this._text);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TooltipFoundation.prototype, "builder", {
        /** Sets the tooltip builder function. */
        set: function (value) {
            this._builder = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TooltipFoundation.prototype, "target", {
        /** Gets/sets the target element CSS selector. */
        get: function () {
            return this._target;
        },
        set: function (value) {
            if (this._target !== value) {
                this._target = value;
                if (this._adapter.hasTargetElement()) {
                    this._removeTargetListeners();
                }
                this._adapter.initializeTargetElement(this._target);
                if (this._adapter.hasTargetElement()) {
                    this._addTargetListeners();
                }
                this._adapter.setHostAttribute(TOOLTIP_CONSTANTS.attributes.TARGET, isDefined(this._target) ? this._target : '');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TooltipFoundation.prototype, "delay", {
        /** Gets/sets the interaction delay. */
        get: function () {
            return this._delay;
        },
        set: function (value) {
            if (this._delay !== value) {
                this._delay = value;
                this._adapter.setHostAttribute(TOOLTIP_CONSTANTS.attributes.DELAY, isDefined(this._delay) ? this._delay.toString() : '');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TooltipFoundation.prototype, "position", {
        /** Gets/sets the tooltip position. */
        get: function () {
            return this._position;
        },
        set: function (value) {
            if (this._position !== value) {
                this._position = value;
                this._adapter.setHostAttribute(TOOLTIP_CONSTANTS.attributes.POSITION, isDefined(this._position) ? this._position.toString() : '');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TooltipFoundation.prototype, "open", {
        get: function () {
            return this._isOpen;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TooltipFoundation.prototype, "tooltipElement", {
        get: function () {
            return this._adapter.getTooltipElement();
        },
        enumerable: true,
        configurable: true
    });
    TooltipFoundation._tooltipIdentifier = 0;
    return TooltipFoundation;
}());
export { TooltipFoundation };
